import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Lock, Eye, Mail, Server, Users, Cookie, Globe } from 'lucide-react';

// Constants outside component to prevent recreation
const LAST_UPDATED = "December 1, 2023";

const POLICY_SECTIONS = [
  {
    icon: <Eye className="w-5 h-5" />,
    title: "Information We Collect",
    content: [
      {
        subtitle: "Personal Information",
        items: [
          "Email address when you contact us or subscribe to updates",
          "Name and company information (if provided)",
          "Communication preferences"
        ]
      },
      {
        subtitle: "Automatically Collected Information",
        items: [
          "IP address and browser type",
          "Device information and operating system",
          "Pages visited and time spent on our site",
          "Referring website and search queries"
        ]
      },
      {
        subtitle: "Cookies and Tracking",
        items: [
          "Session cookies for site functionality",
          "Analytics cookies to improve user experience",
          "Preference cookies to remember your settings"
        ]
      }
    ]
  },
  {
    icon: <Server className="w-5 h-5" />,
    title: "How We Use Your Information",
    content: [
      {
        subtitle: "",
        items: [
          "Provide and maintain our AI tools directory services",
          "Improve website functionality and user experience",
          "Respond to your inquiries and provide support",
          "Send updates about new AI tools and features (with your consent)",
          "Analyze usage patterns to enhance our services",
          "Prevent fraud and ensure platform security"
        ]
      }
    ]
  },
  {
    icon: <Users className="w-5 h-5" />,
    title: "Information Sharing",
    content: [
      {
        subtitle: "We do not sell your personal information",
        items: [
          "Service providers who assist in website operations",
          "Legal authorities when required by law",
          "Third-party analytics providers (anonymous data only)",
          "Business transfers in case of merger or acquisition"
        ]
      }
    ]
  },
  {
    icon: <Shield className="w-5 h-5" />,
    title: "Data Security",
    content: [
      {
        subtitle: "",
        items: [
          "SSL encryption for all data transmissions",
          "Regular security assessments and monitoring",
          "Limited access to personal information",
          "Secure data storage practices",
          "Regular backups and disaster recovery plans"
        ]
      }
    ]
  },
  {
    icon: <Globe className="w-5 h-5" />,
    title: "Your Rights",
    content: [
      {
        subtitle: "",
        items: [
          "Access and review your personal information",
          "Correct inaccurate data",
          "Request deletion of your information",
          "Opt-out of marketing communications",
          "Data portability requests",
          "Withdraw consent at any time"
        ]
      }
    ]
  },
  {
    icon: <Cookie className="w-5 h-5" />,
    title: "Cookies Policy",
    content: [
      {
        subtitle: "Types of Cookies We Use",
        items: [
          "Essential cookies: Required for basic site functionality",
          "Performance cookies: Help us understand how visitors interact",
          "Functionality cookies: Remember your preferences",
          "Analytics cookies: Anonymous usage data collection"
        ]
      },
      {
        subtitle: "Cookie Management",
        items: [
          "You can disable cookies through browser settings",
          "Opt-out of non-essential cookies via our cookie banner",
          "Note: Some features may not work without essential cookies"
        ]
      }
    ]
  }
] as const;

const ADDITIONAL_SECTIONS = [
  {
    title: "Children's Privacy",
    icon: <Users className="w-4 h-4 text-blue-500" />,
    content: "Our service is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us."
  },
  {
    title: "International Transfers",
    icon: <Globe className="w-4 h-4 text-green-500" />,
    content: "Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this privacy policy."
  }
] as const;

const PrivacyPolicy: React.FC = () => {
  // Memoized SEO data
  const seoData = useMemo(() => ({
    title: 'Privacy Policy | TooliQ.ai',
    description: 'TooliQ.ai Privacy Policy - Learn how we collect, use, and protect your personal information while providing AI tools directory services.',
    canonical: 'https://tooliq.ai/privacy-policy',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Privacy Policy",
      "description": "TooliQ.ai Privacy Policy - Learn how we collect, use, and protect your personal information.",
      "url": "https://tooliq.ai/privacy-policy",
      "lastReviewed": LAST_UPDATED,
      "publisher": {
        "@type": "Organization",
        "name": "TooliQ.ai",
        "url": "https://tooliq.ai"
      }
    }
  }), []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <link rel="canonical" href={seoData.canonical} />
        
        {/* Open Graph */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonical} />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(seoData.structuredData)}
        </script>
      </Helmet>

      {/* Header Section */}
      <HeaderSection />

      {/* Main Content */}
      <div className="relative max-w-6xl mx-auto px-6 pb-20">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <SidebarNavigation />

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            <IntroductionSection />
            
            {/* Policy Sections */}
            {POLICY_SECTIONS.map((section, index) => (
              <PolicySection 
                key={index}
                index={index}
                section={section}
              />
            ))}

            {/* Additional Sections */}
            <AdditionalSections />

            {/* Policy Updates */}
            <PolicyUpdatesSection />

            {/* Contact Information */}
            <ContactInformationSection />
          </div>
        </div>
      </div>
    </div>
  );
};

// Extracted HeaderSection component
const HeaderSection: React.FC = () => (
  <div className="relative overflow-hidden">
    <div className="absolute inset-0 bg-grid-slate-200 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-slate-800"></div>
    <div className="relative max-w-6xl mx-auto px-6 py-16 text-center">
      <div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
        <Lock className="w-4 h-4" />
        Your Privacy Matters
      </div>
      <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-slate-800 via-purple-600 to-blue-600 dark:from-slate-100 dark:via-purple-400 dark:to-blue-400 bg-clip-text text-transparent mb-6">
        Privacy Policy
      </h1>
      <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
        We are committed to protecting your privacy and being transparent about how we handle your information. 
        This policy explains our practices in clear, straightforward language.
      </p>
      <div className="mt-6 text-sm text-slate-500 dark:text-slate-400">
        Last updated: {LAST_UPDATED}
      </div>
    </div>
  </div>
);

// Extracted SidebarNavigation component
const SidebarNavigation: React.FC = () => (
  <div className="lg:col-span-1">
    <div className="sticky top-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 p-6">
      <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2">
        <Shield className="w-4 h-4 text-purple-500" />
        Policy Sections
      </h3>
      <nav className="space-y-2">
        {POLICY_SECTIONS.map((section, index) => (
          <a
            key={index}
            href={`#section-${index}`}
            className="block text-sm text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 py-2 px-3 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
          >
            {section.title}
          </a>
        ))}
      </nav>
      
      {/* Quick Contact */}
      <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
        <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-3 text-sm">
          Questions?
        </h4>
        <a
          href="mailto:privacy@tooliq.ai"
          className="inline-flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
        >
          <Mail className="w-4 h-4" />
          privacy@tooliq.ai
        </a>
      </div>
    </div>
  </div>
);

// Extracted IntroductionSection component
const IntroductionSection: React.FC = () => (
  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-lg border border-slate-200 dark:border-slate-700 p-8">
    <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-4">
      Introduction
    </h2>
    <div className="prose dark:prose-invert max-w-none text-slate-700 dark:text-slate-300 leading-relaxed">
      <p>
        At TooliQ.ai, we respect your privacy and are committed to protecting your personal data. 
        This privacy policy will inform you about how we look after your personal data when you visit 
        our website and tell you about your privacy rights and how the law protects you.
      </p>
      <p className="mt-4">
        This policy applies to information we collect:
      </p>
      <ul className="mt-2 space-y-1">
        <li>On this website (tooliq.ai)</li>
        <li>In email, text, and other electronic messages between you and this website</li>
        <li>When you interact with our advertising and applications on third-party websites</li>
      </ul>
    </div>
  </div>
);

// Extracted PolicySection component
interface PolicySectionProps {
  index: number;
  section: typeof POLICY_SECTIONS[number];
}

const PolicySection: React.FC<PolicySectionProps> = ({ index, section }) => (
  <div 
    id={`section-${index}`}
    className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-lg border border-slate-200 dark:border-slate-700 p-8 scroll-mt-20"
  >
    <div className="flex items-center gap-3 mb-6">
      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg text-purple-600 dark:text-purple-400">
        {section.icon}
      </div>
      <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
        {section.title}
      </h2>
    </div>

    <div className="space-y-6">
      {section.content.map((contentBlock, blockIndex) => (
        <div key={blockIndex}>
          {contentBlock.subtitle && (
            <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-3 text-lg">
              {contentBlock.subtitle}
            </h3>
          )}
          <ul className="space-y-2">
            {contentBlock.items.map((item, itemIndex) => (
              <li key={itemIndex} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

// Extracted AdditionalSections component
const AdditionalSections: React.FC = () => (
  <div className="grid md:grid-cols-2 gap-6">
    {ADDITIONAL_SECTIONS.map((section, index) => (
      <div key={index} className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="font-bold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
          {section.icon}
          {section.title}
        </h3>
        <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
          {section.content}
        </p>
      </div>
    ))}
  </div>
);

// Extracted PolicyUpdatesSection component
const PolicyUpdatesSection: React.FC = () => (
  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-800 p-6">
    <h3 className="font-bold text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2">
      <Eye className="w-4 h-4" />
      Policy Updates
    </h3>
    <p className="text-blue-700 dark:text-blue-400 text-sm leading-relaxed">
      We may update this privacy policy from time to time. The updated version will be 
      indicated by an updated "Last updated" date and the updated version will be 
      effective as soon as it is accessible. We encourage you to review this privacy 
      policy frequently to stay informed about how we are protecting your information.
    </p>
  </div>
);

// Extracted ContactInformationSection component
const ContactInformationSection: React.FC = () => (
  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-lg border border-slate-200 dark:border-slate-700 p-8 text-center">
    <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-4">
      Contact Us
    </h2>
    <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-2xl mx-auto">
      If you have any questions about this Privacy Policy or our data practices, 
      please don't hesitate to contact our privacy team.
    </p>
    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
      <a
        href="mailto:privacy@tooliq.ai"
        className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
        aria-label="Email privacy team"
      >
        <Mail className="w-4 h-4" />
        Email Privacy Team
      </a>
      <a
        href="/contact"
        className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 font-semibold py-3 px-6 rounded-xl transition-colors"
        aria-label="Go to contact form"
      >
        Contact Form
      </a>
    </div>
  </div>
);

export default React.memo(PrivacyPolicy);